import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Plus, Check, Gift, Star, Loader2, X,
  Coffee, UtensilsCrossed, IceCream2, ShoppingBag, Palette,
  Clapperboard, Dumbbell, Smartphone, Zap, Sparkles,
} from "lucide-react";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";

const CATEGORIES = [
  { id: "", label: "All", icon: Gift },
  { id: "Cafes & Drinks", label: "Coffee", icon: Coffee },
  { id: "Fast Food", label: "Fast Food", icon: Zap },
  { id: "Restaurant", label: "Restaurant", icon: UtensilsCrossed },
  { id: "Dessert & Ice Cream", label: "Dessert", icon: IceCream2 },
  { id: "Bakery", label: "Bakery", icon: Sparkles },
  { id: "Retail", label: "Retail", icon: ShoppingBag },
  { id: "Beauty & Skincare", label: "Beauty", icon: Palette },
  { id: "Entertainment", label: "Fun", icon: Clapperboard },
  { id: "Health & Fitness", label: "Fitness", icon: Dumbbell },
];

export default function BrowseOffers({ onAdd, onClose, selectedCategories = [], themeColor }) {
  const { getStatus } = useUnifiedStatus();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [recommended, setRecommended] = useState([]);
  const [loadingRec, setLoadingRec] = useState(false);

  // Fetch recommended offers (categories user hasn't picked)
  useEffect(() => {
    if (!selectedCategories?.length) return;
    const allCats = CATEGORIES.filter(c => c.id).map(c => c.id);
    const missingCats = allCats.filter(c => !selectedCategories.includes(c));
    if (missingCats.length === 0) return;

    setLoadingRec(true);
    axios.get(`${API}/itinerary/browse-offers`, {
      params: { category: missingCats.slice(0, 3).join(","), limit: 5, nearby_only: true }
    }).then(res => {
      setRecommended((res.data.offers || []).filter(o => !o.in_itinerary).slice(0, 4)).catch(() => {});
    }).catch(() => {}).finally(() => setLoadingRec(false));
  }, [selectedCategories]);

  const fetchOffers = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (query) params.q = query;
      if (category) params.category = category;
      const res = await axios.get(`${API}/itinerary/browse-offers`, { params });
      setOffers(res.data.offers);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      setPage(pg);
    } catch {}
    finally { setLoading(false); }
  }, [query, category]);

  useEffect(() => {
    const t = setTimeout(() => fetchOffers(1), 300);
    return () => clearTimeout(t);
  }, [fetchOffers]);

  const addToItinerary = async (businessId, isRec = false) => {
    setAdding(businessId);
    try {
      await onAdd(businessId);
      if (isRec) setRecommended(prev => prev.filter(o => o.business_id !== businessId));
      else setOffers(prev => prev.map(o => o.business_id === businessId ? { ...o, in_itinerary: true } : o));
      toast.success("Added to your itinerary!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add";
      toast.error(msg);
    }
    finally { setAdding(null); }
  };

  const accent = themeColor || "#ec4899";

  return (
    <div className="fixed inset-0 z-[150] bg-white flex flex-col" data-testid="browse-offers-panel">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black text-slate-800">Browse Offers</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="browse-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': `${accent}33` }}
            data-testid="browse-search"
          />
        </div>
        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const sel = category === cat.id;
            return (
              <button key={cat.id} onClick={() => setCategory(cat.id)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold flex items-center gap-1 border transition ${sel ? "text-white" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`} style={sel ? { backgroundColor: accent, borderColor: accent } : {}} data-testid={`browse-cat-${cat.id || "all"}`}>
                <Icon className="w-3 h-3" /> {cat.label}
              </button>
            );
          })}
        </div>
        <p className="text-[9px] text-slate-400 mt-2">{total} offers available</p>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {/* Recommended For You */}
        {recommended.length > 0 && !query && !category && (
          <div className="mb-4" data-testid="recommended-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5" style={{ color: accent }} />
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Recommended for you</p>
            </div>
            <p className="text-[9px] text-slate-400 mb-2">High-value offers from categories you haven't picked yet</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {recommended.map(offer => (
                <div key={offer.business_id} className="flex-shrink-0 w-40 border border-slate-100 rounded-xl p-2.5 bg-gradient-to-br from-white to-slate-50" data-testid={`rec-offer-${offer.business_id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {offer.logo_url ? <img src={offer.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-4 h-4 text-slate-300" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-700 truncate">{offer.name}</p>
                      {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                      <p className="text-[9px] text-slate-400 truncate">{offer.category}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 line-clamp-1 mb-1.5">{offer.offer_title}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-600">${offer.value_est}</span>
                    <button onClick={() => addToItinerary(offer.business_id, true)} disabled={adding === offer.business_id} className="px-2 py-1 text-white text-[9px] font-bold rounded-lg flex items-center gap-0.5" style={{ backgroundColor: accent }} data-testid={`rec-add-${offer.business_id}`}>
                      {adding === offer.business_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Plus className="w-2.5 h-2.5" />} Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && offers.length === 0 && (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} /></div>
        )}

        <AnimatePresence>
          {offers.map(offer => (
            <motion.div key={offer.business_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-100 rounded-xl p-3 flex items-center gap-3" data-testid={`browse-offer-${offer.business_id}`}>
              <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer" onClick={() => navigate(`/offer/${offer.business_id}`)}>
                {offer.logo_url ? <img src={offer.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/offer/${offer.business_id}`)}>
                <p className="text-sm font-bold text-slate-800 truncate">{offer.name}</p>
                {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                <p className="text-[10px] text-slate-400 line-clamp-1">{offer.offer_title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-bold text-emerald-600">${offer.value_est}</span>
                  <span className="text-[9px] text-slate-300">|</span>
                  <span className="text-[9px] text-slate-400 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />{offer.ease_stars}</span>
                  {offer.requires_app && <Smartphone className="w-2.5 h-2.5 text-sky-400" />}
                </div>
              </div>
              {offer.in_itinerary ? (
                <span className="px-3 py-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-lg flex items-center gap-1" data-testid={`browse-added-${offer.business_id}`}>
                  <Check className="w-3 h-3" /> Added
                </span>
              ) : (
                <button onClick={() => addToItinerary(offer.business_id)} disabled={adding === offer.business_id} className="px-3 py-1.5 text-white text-[10px] font-bold rounded-lg flex items-center gap-1 disabled:opacity-50" style={{ backgroundColor: accent }} data-testid={`browse-add-${offer.business_id}`}>
                  {adding === offer.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-3">
            <button onClick={() => fetchOffers(page - 1)} disabled={page <= 1} className="px-3 py-1.5 text-xs font-bold bg-slate-100 rounded-lg disabled:opacity-30" data-testid="browse-prev">Prev</button>
            <span className="text-xs text-slate-400">{page} / {totalPages}</span>
            <button onClick={() => fetchOffers(page + 1)} disabled={page >= totalPages} className="px-3 py-1.5 text-xs font-bold bg-slate-100 rounded-lg disabled:opacity-30" data-testid="browse-next">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
